const STORAGE_KEY = 'eventsbridge:favorites:v1';
const ID_STRATEGY = 'composite_v1';

let favoritesState = loadFavoritesInternal();
let favoritesSet = new Set(Object.keys(favoritesState.favoritesByKey || {}));

function loadFavoritesInternal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: 1, favoritesByKey: {} };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { version: 1, favoritesByKey: {} };
    if (!parsed.version) parsed.version = 1;
    if (!parsed.favoritesByKey) parsed.favoritesByKey = {};
    return parsed;
  } catch {
    return { version: 1, favoritesByKey: {} };
  }
}

function saveFavoritesInternal() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(favoritesState));
}

function normalizeTitle(title) {
  return (title || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeLocation(loc) {
  const s = (loc || '').toString().trim();
  if (!s || s.toLowerCase() === 'location not found') return '';
  return s.replace(/\s+/g, ' ');
}

function parseStartTime(timeText) {
  if (!timeText) return null;
  const s = timeText.toString().trim().toLowerCase();
  const firstPart = s.split(/–|-|to/)[0]?.trim() || s;
  // Match formats like "6pm", "6:00 pm", "10 am"
  const m = firstPart.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
  if (!m) return null;
  let hours = parseInt(m[1], 10);
  const minutes = m[2] ? parseInt(m[2], 10) : 0;
  const ampm = (m[3] || '').toLowerCase();
  if (ampm === 'pm' && hours < 12) hours += 12;
  if (ampm === 'am' && hours === 12) hours = 0;
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  return `${hh}:${mm}`;
}

function simpleHash(str) {
  // 32-bit FNV-1a
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16);
}

export function getEventKeyFromEvent(event) {
  const source = (event.source || '').toString();
  const date = (event.Date || '').toString();
  const start = parseStartTime(event.Time) || '00:00';
  const normalizedTitle = normalizeTitle(event['Event Name'] || '');
  const loc = normalizeLocation(event.Location);
  const eventType = (event.eventType || '').toString().trim();
  const identity = `${date}|${start}|${normalizedTitle}|${loc}|${eventType}`;
  return `${source}:${simpleHash(identity)}`;
}

// Public share-safe ID derived from the internal event key.
// This hides the source prefix (e.g. "tangomango") from URLs while
// remaining stable as long as getEventKeyFromEvent's behavior is stable.
export function getShareIdForEvent(event) {
  const internalKey = getEventKeyFromEvent(event);
  return simpleHash(internalKey);
}

export function buildSnapshotFromEvent(event) {
  const start = parseStartTime(event.Time);
  let startDateTimeISO = null;
  if (event.Date && start) {
    // Interpret as local time; sufficient for local-only persistence
    const d = new Date(`${event.Date}T${start}:00`);
    if (!isNaN(d.getTime())) startDateTimeISO = d.toISOString();
  }
  let externalUrl = null;
  if (event.Details && typeof event.Details === 'string' && event.Details.startsWith('http')) {
    externalUrl = event.Details;
  }
  return {
    key: getEventKeyFromEvent(event),
    source: event.source || '',
    title: event['Event Name'] || '',
    date: event.Date || '',
    timeText: event.Time || null,
    startDateTimeISO,
    endDateTimeISO: null,
    location: event.Location || null,
    description: event.Description || null,
    price: event.price || null,
    eventType: event.eventType || null,
    externalUrl,
    addedAt: new Date().toISOString(),
    lastSeenAt: null,
    lastSeenDatasetDate: null
  };
}

export function isFavorite(eventKey) {
  return favoritesSet.has(eventKey);
}

export function addFavorite(snapshot) {
  favoritesState.favoritesByKey[snapshot.key] = snapshot;
  favoritesSet.add(snapshot.key);
  saveFavoritesInternal();
}

export function removeFavorite(eventKey) {
  delete favoritesState.favoritesByKey[eventKey];
  favoritesSet.delete(eventKey);
  saveFavoritesInternal();
}

export function toggleFavorite(snapshot) {
  if (isFavorite(snapshot.key)) {
    removeFavorite(snapshot.key);
  } else {
    addFavorite(snapshot);
  }
}

export function getFavoritesList({ includePast = false, includeRemoved = false, todayDatasetDate = null } = {}) {
  const list = Object.values(favoritesState.favoritesByKey);
  return list.filter(item => {
    // Filter removed
    if (!includeRemoved && todayDatasetDate && item.lastSeenDatasetDate !== todayDatasetDate) {
      return false;
    }
    // Filter past (only if we can compare date reliably)
    if (!includePast && item.date) {
      const today = new Date();
      const ymd = today.toISOString().slice(0, 10);
      if (item.date < ymd) return false;
    }
    return true;
  });
}

export function getFavoritesCount() {
  return favoritesSet.size;
}

export function reconcileWithDataset({ datasetDate, currentEventKeys }) {
  const nowIso = new Date().toISOString();
  Object.keys(favoritesState.favoritesByKey).forEach(k => {
    if (currentEventKeys.has(k)) {
      const snap = favoritesState.favoritesByKey[k];
      snap.lastSeenAt = nowIso;
      snap.lastSeenDatasetDate = datasetDate;
    }
  });
  saveFavoritesInternal();
}

export function getIdStrategy() {
  return ID_STRATEGY;
}

